import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Card, Modal } from "react-bootstrap";
import SmuTable from "../smu-table/SmuTable";
import SmuSearch from "../smu-search/SmuSearch";
import { prepareText } from "../../helpers";
import { TfiReload } from "react-icons/tfi";
import { FaHistory } from "react-icons/fa";
import { StoreContext } from "../../store/StoreContext";
import { fetchPreviousVersionData } from "../../api/files";
import { COLOR_PRIMARY } from "../../constants/colors";

// Randomizer para los codigos de barra
const seedFromString = (s) => {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = (Math.imul(31, hash) + s.charCodeAt(i)) | 0;
  }
  return hash >>> 0;
};

const mulberry32 = (a) => () => {
  let t = (a += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const generateBarcodeGradient = (key) => {
  const rand = mulberry32(seedFromString(String(key || "barcode")));
  const max = 120; // px for one pattern repeat
  let pos = 0;
  const parts = [];
  while (pos < max) {
    const bar = 1 + Math.floor(rand() * 3); // 1-3px
    const gap = 1 + Math.floor(rand() * 3); // 1-3px
    const endBar = Math.min(pos + bar, max);
    const endGap = Math.min(endBar + gap, max);
    parts.push(`#000 ${pos}px ${endBar}px`, `transparent ${endBar}px ${endGap}px`);
    pos = endGap;
  }
  return `repeating-linear-gradient(90deg, ${parts.join(", ")})`;
};

// Helper function to parse date string as UTC and format to Chile timezone
const formatDateToChileTime = (dateString) => {
  if (!dateString) return '';
  // If the string doesn't have a timezone indicator, treat it as UTC by appending 'Z'
  // This handles cases like "2025-11-20T13:10:10.352815" which should be parsed as UTC
  const utcDateString = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString) 
    ? dateString 
    : dateString + 'Z';
  return new Date(utcDateString).toLocaleString('es-CL', { timeZone: 'America/Santiago' });
};

/**
 * SmuSkirtCards
 * Renders a grid of skirt cards with search/filter and selection.
 * When pressing "Ver detalles" on a card, opens a modal with a table
 * (SmuTable) mapping the same columns as ProductsTable.
 *
 * Props:
 * - items: Array<{
 *     skirt_name: string,
 *     product_count: number,
 *     products: Product[]
 *   }>
 * - filter: string (optional, external controlled filter text)
 * - onFilterChange: (text: string) => void (optional)
 * - defaultSelected: string[] (optional, skirt_name list)
 * - onSelectionChange: (selectedItems: Array<typeof items[number]>) => void (optional)
 */
const SmuSkirtCards = ({
  items = [],
  filter,
  onFilterChange,
  defaultSelected = [],
  onSelectionChange,
  promotionId,
  filterCriteria = {},
  setVisibleFaldones,
}) => {
  const { loggedUser, isAdmin } = useContext(StoreContext);
  // internal filter if not controlled from outside
  const [internalFilter, setInternalFilter] = useState(filter || "");
  useEffect(() => {
    if (filter !== undefined) setInternalFilter(filter || "");
  }, [filter]);

  const handleFilterChange = (text) => {
    if (onFilterChange) onFilterChange(text);
    if (filter === undefined) setInternalFilter(text);
  };

  // selection management
  const [selectedNames, setSelectedNames] = useState(new Set(defaultSelected));
  useEffect(() => {
    if (onSelectionChange) {
      const selectedItems = items.filter((g) => selectedNames.has(g.skirt_name));
      onSelectionChange(selectedItems);
    }
  }, [selectedNames, items]);

  const toggleSelected = (groupName) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  // Helper function to check if a product matches the filter criteria
  const productMatchesFilters = useCallback((product, criteria, searchTerm) => {
    // Check search term first (if exists)
    if (searchTerm) {
      const _producto = prepareText(product.producto || "");
      const _marca = prepareText(product.marca || "");
      const _categoria = prepareText(product.categoria || "");
      const _ean = prepareText(product.ean13 || "");
      
      const matchesSearch = (
        _producto.includes(searchTerm) ||
        _marca.includes(searchTerm) ||
        _categoria.includes(searchTerm) ||
        _ean.includes(searchTerm)
      );
      
      if (!matchesSearch) return false;
    }
    
    // Check filter criteria
    if (criteria.producto) {
      const _producto = prepareText(product.producto || "");
      const _criteriaProducto = prepareText(criteria.producto);
      if (!_producto.includes(_criteriaProducto)) return false;
    }
    
    if (criteria.marca) {
      const _marca = prepareText(product.marca || "");
      const _criteriaMarca = prepareText(criteria.marca);
      if (!_marca.includes(_criteriaMarca)) return false;
    }
    
    if (criteria.categoria) {
      const _categoria = prepareText(product.categoria || "");
      const _criteriaCategoria = prepareText(criteria.categoria);
      if (!_categoria.includes(_criteriaCategoria)) return false;
    }
    
    if (criteria.ean) {
      const _ean = prepareText(product.ean13 || "");
      const _criteriaEan = prepareText(criteria.ean);
      if (!_ean.includes(_criteriaEan)) return false;
    }
    
    return true;
  }, []);

  // filter cards by visible fields: faldon (skirt_name), product_count, and product fields
  const filteredCards = useMemo(() => {
    const term = prepareText((internalFilter || "").trim());
    const hasSearchTerm = !!term;
    const hasFilterCriteria = !!(filterCriteria.producto || filterCriteria.marca || filterCriteria.categoria || filterCriteria.ean);
    
    // If no filters at all, return all items
    if (!hasSearchTerm && !hasFilterCriteria) {
      setVisibleFaldones(items);
      return items;
    }
    
    let filteredItems = items.filter((group) => {
      // If filter criteria exist, check if any product matches ALL criteria
      if (hasFilterCriteria) {
        if (!group.products || !Array.isArray(group.products) || group.products.length === 0) {
          return false;
        }
        
        // Check if at least one product matches ALL filter criteria (and search term if exists)
        const hasMatchingProduct = group.products.some((p) => 
          productMatchesFilters(p, filterCriteria, term)
        );
        
        if (!hasMatchingProduct) return false;
      }
      
      // If only search term exists (no filter criteria), use original logic
      if (hasSearchTerm && !hasFilterCriteria) {
        // Check faldon (skirt_name)
        const _faldon = prepareText(group.skirt_name || "");
        if (_faldon.includes(term)) return true;
        
        // Check product_count
        const _productCount = prepareText(String(group.product_count || ""));
        if (_productCount.includes(term)) return true;
        
        // Check if any product in the group matches the search term
        if (group.products && Array.isArray(group.products)) {
          const hasMatchingProduct = group.products.some((p) => {
            const _producto = prepareText(p.producto || "");
            const _marca = prepareText(p.marca || "");
            const _categoria = prepareText(p.categoria || "");
            const _ean = prepareText(p.ean13 || "");
            
            return (
              _producto.includes(term) ||
              _marca.includes(term) ||
              _categoria.includes(term) ||
              _ean.includes(term)
            );
          });
          
          if (hasMatchingProduct) return true;
        }
        
        return false;
      }
      
      // If we have filter criteria and they matched, return true
      return true;
    });
    setVisibleFaldones(filteredItems);
    return filteredItems;
  }, [items, internalFilter, filterCriteria, productMatchesFilters]);

  // details modal state
  const [showModal, setShowModal] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [currentProducts, setCurrentProducts] = useState([]);
  const [modalSearch, setModalSearch] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [previousVersionData, setPreviousVersionData] = useState([]);
  const [loadingPreviousVersion, setLoadingPreviousVersion] = useState(false);
  const [previousVersionError, setPreviousVersionError] = useState(null);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  // Helper function to generate unique key for product comparison
  const getProductKey = (product) => {
    const keyParts = [
      product?.faldon ?? "NO_FALDON",
      product?.ean13 ?? product?.producto ?? product?.id ?? "NO_ID",
    ];
    return keyParts.join("|");
  };

  const openDetails = (group) => {
    setActiveGroup(group);
    // Save current products data
    setCurrentProducts(group.products ? [...group.products] : []);
    setModalSearch(internalFilter || "");
    setShowHistory(false);
    setPreviousVersionData([]); // Reset previous version data when opening modal
    setPreviousVersionError(null); // Reset error when opening modal
    setHasAttemptedFetch(false); // Reset fetch attempt flag
    setShowModal(true);
  };

  const closeDetails = () => {
    setShowModal(false);
    setActiveGroup(null);
    setShowHistory(false);
    setPreviousVersionData([]); // Reset previous version data when closing modal
    setPreviousVersionError(null); // Reset error when closing modal
    setHasAttemptedFetch(false); // Reset fetch attempt flag
  };

  const handleHistoryClick = () => {
    setShowHistory((prev) => {
      const newValue = !prev;
      if (!newValue) {
        // Reset error and fetch attempt when turning history off
        setPreviousVersionError(null);
        setHasAttemptedFetch(false);
      } else {
        // Reset fetch attempt when turning history on, so we can fetch again
        setHasAttemptedFetch(false);
      }
      return newValue;
    });
  };

  // Map of previous version data (from API only)
  const previousVersionMap = useMemo(() => {
    if (!currentProducts || !Array.isArray(currentProducts) || !Array.isArray(previousVersionData) || previousVersionData.length === 0) return {};
    const map = {};
    currentProducts.forEach((product) => {
      const currentVersion = Number(product?.version);
      if (currentVersion > 1) {
        // Match by faldon, version, AND product identifier (EAN/producto/id) to ensure correct matching
        const productIdentifier = product?.ean13 ?? product?.producto ?? product?.id;
        const previous = previousVersionData.find(
          (item) => {
            const itemIdentifier = item?.ean13 ?? item?.producto ?? item?.id;
            return (
              item.faldon === product.faldon &&
              Number(item.version) === currentVersion - 1 &&
              itemIdentifier === productIdentifier
            );
          }
        );
        if (previous) {
          const key = getProductKey(product);
          if (key) {
            map[key] = previous;
          }
        }
      }
    });
    return map;
  }, [currentProducts, previousVersionData]);

  // Map of all previous version products (for finding deleted ones)
  // Fetch previous version data when history is clicked
  useEffect(() => {
    // Only fetch if history is on, we have data, not currently loading, and haven't already attempted (or succeeded)
    if (showHistory && activeGroup && currentProducts.length > 0 && !loadingPreviousVersion && !hasAttemptedFetch) {
      const faldonName = activeGroup?.skirt_name || activeGroup?.products?.[0]?.faldon;
      // Get faldon id from promotionId (same as "Id Promoción" in main view)
      // Remove .xlsx/.xls extension if present
      const faldonId = promotionId ? promotionId.replace(/\.(xlsx|xls)$/i, '') : null;
      const username = loggedUser?.username;
      const formatoImpresion = currentProducts[0]?.formatoImpresion;
      const negocio = currentProducts[0]?.negocio;
      // Get the highest version from current products to determine previous version
      const maxVersion = Math.max(...currentProducts.map(p => Number(p?.version) || 1));
      
      if (maxVersion <= 1) {
        setPreviousVersionError('No hay versión anterior disponible');
        setHasAttemptedFetch(true);
        return;
      }
      
      if (!faldonId || !faldonName || !username) {
        setPreviousVersionError('Faltan datos necesarios para cargar la versión anterior');
        setHasAttemptedFetch(true);
        return;
      }

      // Mark that we're attempting to fetch
      setHasAttemptedFetch(true);
      setLoadingPreviousVersion(true);
      setPreviousVersionError(null);
      fetchPreviousVersionData(faldonId, faldonName, username, maxVersion, formatoImpresion, negocio)
        .then(({ data }) => {
          // Only update data on successful response
          const apiData = Array.isArray(data) ? data : [];
          if (apiData.length === 0) {
            setPreviousVersionError('No se encontraron datos de la versión anterior');
            // Don't update previousVersionData if empty - keep it as is
          } else {
            setPreviousVersionData(apiData);
            setPreviousVersionError(null); // Clear any previous errors on success
          }
        })
        .catch((error) => {
          console.error('Error fetching previous version:', error);
          // Don't modify previousVersionData on error - keep existing data or empty
          setPreviousVersionError('Error al cargar la versión anterior. Por favor, intente nuevamente.');
        })
        .finally(() => {
          setLoadingPreviousVersion(false);
        });
    }
  }, [showHistory, activeGroup, currentProducts, loggedUser, loadingPreviousVersion, hasAttemptedFetch, promotionId]);
  
  const previousProductsMap = useMemo(() => {
    if (!activeGroup || !Array.isArray(previousVersionData) || previousVersionData.length === 0) return {};
    const map = {};
    // Get all previous version products for this faldon
    const faldonName = activeGroup?.skirt_name || activeGroup?.products?.[0]?.faldon;
    if (faldonName) {
      previousVersionData.forEach((item) => {
        if (item.faldon === faldonName) {
          const key = getProductKey(item);
          if (key) {
            map[key] = item;
          }
        }
      });
    }
    return map;
  }, [activeGroup, previousVersionData]);

  // Function to determine change status for a product
  const getChangeStatus = useCallback((product) => {
    if (!showHistory) return null;
    
    // Deleted: product is in previous but not in current
    if (product._isDeleted) {
      return "Eliminado";
    }
    
    const key = getProductKey(product);
    const previous = previousProductsMap[key];
    
    // Added: in current but not in previous
    if (!previous) {
      return "Añadido";
    }
    
    // Check if precioModal, precioOfertaUnitario, or porcentaje changed
    const normalize = (p) => String(p||'').replace(/[^\d.]/g, '');
    const normalizeText = (p) => String(p || '').trim().toLowerCase();
    const precioModalChanged = normalize(product?.precioModal) !== normalize(previous?.precioModal);
    const precioUnitarioChanged = normalize(product?.precioOfertaUnitario) !== normalize(previous?.precioOfertaUnitario);
    const porcentajeChanged = normalize(product?.porcentaje) !== normalize(previous?.porcentaje);
    const productoChanged = normalizeText(product?.producto) !== normalizeText(previous?.producto);
    const marcaChanged = normalizeText(product?.marca) !== normalizeText(previous?.marca);
    const categoriaChanged = normalizeText(product?.categoria) !== normalizeText(previous?.categoria);
    // Modified: in both but prices, porcentaje, or descriptive fields changed
    if (
      precioModalChanged ||
      precioUnitarioChanged ||
      porcentajeChanged ||
      productoChanged ||
      marcaChanged ||
      categoriaChanged
    ) {
      return "Modificado";
    }
    
    // Unchanged: in both with no changes
    return "Sin modificación";
  }, [showHistory, previousProductsMap]);

  const renderPriceWithHistory = useCallback(
    (row, currentValue, field) => {
      const keyParts = [
        row?.faldon ?? "NO_FALDON",
        row?.ean13 ?? row?.producto ?? row?.id ?? "NO_ID",
      ];
      const key = keyParts.join("|");
      const previous = previousVersionMap[key];
      
      // Only show strikethrough if: 1. History is enabled, 2. Product version > 1,
      // 3. Previous version exists, 4. Values are actually different
      if (showHistory && Number(row?.version) > 1 && previous && previous[field] !== undefined) {
        const normalize = (val) => String(val || '').replace(/[^\d.]/g, '');
        const currentNormalized = normalize(currentValue);
        const previousNormalized = normalize(previous[field]);
        const valuesDiffer = currentNormalized !== previousNormalized;
        
        if (valuesDiffer) {
          return (
            <div className="cell d-flex align-items-center">
              <span className="text-muted text-decoration-line-through me-2">
                {previous[field]}
              </span>
              <span>{currentValue ?? "N/A"}</span>
            </div>
          );
        }
      }
      return <div className="cell">{currentValue ?? "N/A"}</div>;
    },
    [previousVersionMap, showHistory]
  );

  // table columns (mirroring ProductsTable without selection column)
  const columns = useMemo(
    () => [
      {
        id: "faldonXlsx",
        name: "Faldon",
        selector: (row) => row.faldonXlsx || row.faldon,
        cell: (row) => {
          // For deleted products, use "faldon" instead of "faldonXlsx"
          const faldonValue = row._isDeleted ? row.faldon : (row.faldonXlsx || row.faldon);
          return <div className="cell">{faldonValue?.replace(/\.(xlsx|xls)$/i, '')}</div>;
        },
        sortable: true,
      },
      {
        name: "Nombre Campaña",
        selector: (row) => row.nombreCampanaProd,
        cell: (row) => <div className="cell">{row.nombreCampanaProd || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "Producto",
        selector: (row) => row.producto,
        cell: (row) => <div className="cell">{row.producto || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "Marca",
        selector: (row) => row.marca,
        cell: (row) => <div className="cell">{row.marca || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "Categoria",
        selector: (row) => row.categoria,
        cell: (row) => <div className="cell">{row.categoria || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "EAN 13",
        selector: (row) => row.ean13,
        cell: (row) => <div className="cell">{row.ean13 || "N/A"}</div>,
        sortable: true,
      },
      {
        name: "Porcentaje",
        selector: (row) => row.porcentaje,
        cell: (row) => renderPriceWithHistory(row, row.porcentaje, "porcentaje"),
        sortable: true,
      },
      {
        name: "Precio Modificado",
        selector: (row) => row.precioModal,
        cell: (row) => renderPriceWithHistory(row, row.precioModal, "precioModal"),
        sortable: true,
      },
      {
        name: "Precio Oferta Unitario",
        selector: (row) => row.precioOfertaUnitario,
        cell: (row) =>
          renderPriceWithHistory(row, row.precioOfertaUnitario, "precioOfertaUnitario"),
        sortable: true,
      },
      // Cambios column - only show when history is active AND we have successfully fetched previous version data
      ...(showHistory && previousVersionData.length > 0 && !previousVersionError ? [{
        name: "Cambios",
        selector: (row) => getChangeStatus(row),
        cell: (row) => {
          const status = getChangeStatus(row);
          if (!status) return <div className="cell">-</div>;
          
          let color = "";
          if (status === "Añadido") color = "text-success";
          else if (status === "Eliminado") color = "text-danger";
          else if (status === "Modificado") color = "text-warning";
          else color = "text-muted";
          return (
            <div className={`cell ${color} ${row._isDeleted ? 'text-decoration-line-through' : ''}`}>
              <span style={{
                backgroundColor: "#f4f4f4",
                padding: "4px 8px",
                borderRadius: "6px",
                display: "inline-block"
              }}>
                {status}
              </span>
            </div>
          );
        },
        sortable: true,
      }] : []),
    ],
    [renderPriceWithHistory, showHistory, getChangeStatus, previousVersionData, previousVersionError]
  );

  const filteredProductsForModal = useMemo(() => {
    if (!activeGroup) return [];
    
    let productsToShow = currentProducts || [];
    
    // When history is shown AND we have successfully fetched previous version data, include deleted products
    if (showHistory && previousVersionData.length > 0 && !previousVersionError) {
      const currentKeys = new Set(currentProducts.map(p => getProductKey(p)));
      const deletedProducts = Object.values(previousProductsMap)
        .filter(prev => !currentKeys.has(getProductKey(prev)))
        .map(prev => ({ ...prev, _isDeleted: true }));
      productsToShow = [...currentProducts, ...deletedProducts];
    }
    
    // Apply search filter
    const term = prepareText((modalSearch || "").trim());
    if (!term) return productsToShow;
    
    return productsToShow.filter((p) => {
      const values = [
        p.faldonXlsx,
        p.producto,
        p.marca,
        p.categoria,
        p.ean13,
      ]
        .filter((v) => v !== null && v !== undefined)
        .map((v) => prepareText(String(v)));
      return values.some((v) => v.includes(term));
    });
  }, [activeGroup, currentProducts, modalSearch, showHistory, previousProductsMap, previousVersionData, previousVersionError]);

  const showHistoryButton = useMemo(() => {
    if (!activeGroup || !Array.isArray(activeGroup.products)) return false;
    return activeGroup.products.some((product) => Number(product?.version) > 1);
  }, [activeGroup]);

  // barcode style factory - randomize stripe widths per card deterministically
  const getBarcodeStyle = (key) => ({
    width: "60%",
    height: 10,
    margin: "0 auto",
    backgroundImage: generateBarcodeGradient(key),
  });

  // Helper function to get the EAN to display based on filter
  const getEanToDisplay = useCallback((group) => {
    if (!group.products || group.products.length === 0) return "N/A";
    
    const filterTerm = prepareText((internalFilter || "").trim());
    const hasSearchTerm = !!filterTerm;
    const hasFilterCriteria = !!(filterCriteria.producto || filterCriteria.marca || filterCriteria.categoria || filterCriteria.ean);
    
    // If no filters at all, return first product's EAN
    if (!hasSearchTerm && !hasFilterCriteria) {
      return group.products[0]?.ean13 || "N/A";
    }
    
    // Find the first product that matches all filter criteria
    const matchingProduct = group.products.find((p) => 
      productMatchesFilters(p, filterCriteria, filterTerm)
    );
    
    // If a matching product is found, return its EAN
    if (matchingProduct) {
      return matchingProduct.ean13 || "N/A";
    }
    
    // If no match found, return first product's EAN as fallback
    return group.products[0]?.ean13 || "N/A";
  }, [internalFilter, filterCriteria, productMatchesFilters]);

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <SmuSearch
          value={internalFilter}
          onChange={({ target }) => handleFilterChange(target.value)}
        />
      </div>

      <div className="row g-3">
        {filteredCards.map((group) => (
          <div key={group.skirt_name} className="col-12 col-sm-6 col-md-4 col-lg-2">
            <Card
              className="h-60 w-55"
              style={{
                backgroundColor: "#fcd34d",
                borderRadius: 20,
                border: "1px solid rgba(0,0,0,0.15)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              }}
            >
              <Card.Body className="d-flex flex-column">
                <div className="d-flex align-items-start mb-1">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedNames.has(group.skirt_name)}
                    onChange={() => toggleSelected(group.skirt_name)}
                    style={{ width: 20, height: 20, cursor: "pointer" }}
                  />
                  {group.products?.some((p) => p?.version && Number(p?.version) > 1) ? (
                    <TfiReload className="ms-auto" />
                  ) : null}
                </div>
                <div className="text-center" style={{ flex: 1 }}>
                  <div>
                    <div style={{ fontSize: 14, color: "#666", marginBottom: 4 }}>EAN:</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}
                        title={
                          (group.products && group.products.length > 0)
                            ? `EANs:\n${group.products
                                .map(p => p.ean13)
                                .filter(Boolean)
                                .join('\n')}`
                            : "N/A"
                        }
                      >
                      {getEanToDisplay(group)}
                    </div>
                  </div>
                  <div className="mt-3">
                    <strong style={{ fontSize: 20 }}>Productos:</strong><br></br>
                    <span style={{ fontSize: 16 }}>{group.product_count}</span>
                  </div>
                </div>
                <div className="text-center mt-1">
                  <button
                    className="smu-generic-button solid-btn px-3 py-2"
                    style={{ background: {COLOR_PRIMARY}, borderColor: {COLOR_PRIMARY} }}
                    onClick={() => openDetails(group)}
                  >
                    <span style={{ color: "#fff" }}>Ver detalles</span>
                  </button>
                </div>
                {/* <div className="mt-1" style={getBarcodeStyle(group.skirt_name)} /> */}
                <span
                  style={{ fontSize: 12, color: "#666", textAlign: "right", cursor: "pointer" }}
                  title={
                    group.products && group.products.length > 0 && group.products[0].fechaVigenciaDesde && group.products[0].fechaVigenciaHasta
                      ? (() => {
                          const formatDate = (dateStr) => {
                            if (!dateStr) return '';
                            const date = new Date(dateStr + "T00:00:00");
                            const day = String(date.getDate()).padStart(2, '0');
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const year = date.getFullYear();
                            return `${day}-${month}-${year}`;
                          };
                          return `Válido del ${formatDate(group.products[0].fechaVigenciaDesde)} al ${formatDate(group.products[0].fechaVigenciaHasta)}`;
                        })()
                      : undefined
                  }
                >
                  Versión: {Math.max(...(group.products || []).map(p => p.version || 0))}
                </span>
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>
      <Modal show={showModal} onHide={closeDetails} size="xl" centered>
        <Modal.Header closeButton className="align-items-center">
          <Modal.Title className="me-3 mb-0">
            {/*{activeGroup 
              ? `Faldón EAN: ${activeGroup.products && activeGroup.products.length > 0 
                  ? (activeGroup.products[0]?.ean13 || "N/A")
                  : "N/A"}`
              : "Detalles"}
            */}
            {activeGroup ? `Faldón ${activeGroup.skirt_name}` : "Detalles"}
          </Modal.Title>
          {showHistoryButton && (
            <button
              type="button"
              className="smu-generic-button solid-btn p-0 me-3 d-flex align-items-center justify-content-center"
              onClick={handleHistoryClick}
              aria-label="Ver historial"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                backgroundColor: "var(--data-color-primary)",
                borderColor: "var(--data-color-primary)",
                color: "#fff"
              }}
            >
              <FaHistory size={18} color="#fff" />
            </button>
          )}
          <div className="ms-auto" style={{ minWidth: 280 }}>
            <SmuSearch value={modalSearch} onChange={({ target }) => setModalSearch(target.value)} />
          </div>
        </Modal.Header>
        <Modal.Body>
          <SmuTable
            columns={columns}
            data={filteredProductsForModal}
            loading={false}
            defaultSortFieldId="faldonXlsx"
            defaultSortAsc={false}
          />
          {/* Version information */}
          {currentProducts.length > 0 && (
            <div className="mt-3 pt-3 border-top">
              {currentProducts[0]?.fechaHoraCarga && currentProducts[0]?.usernameCarga && (
                <div className="text-muted small">
                  <strong>Versión actual:</strong> {formatDateToChileTime(currentProducts[0].fechaHoraCarga)}{isAdmin && ` subida por ${currentProducts[0].usernameCarga}`}
                </div>
              )}
              {showHistory && (() => {
                // Show error if API call failed or no data available
                if (previousVersionError) {
                  return (
                    <div className="text-danger small mt-2">
                      <strong>Error:</strong> {previousVersionError}
                    </div>
                  );
                }
                // Show loading state
                if (loadingPreviousVersion) {
                  return (
                    <div className="text-muted small mt-2">
                      Cargando versión anterior...
                    </div>
                  );
                }
                // Get previous version info from API data
                if (currentProducts.length > 0 && currentProducts[0]?.faldon && previousVersionData.length > 0) {
                  const faldonName = currentProducts[0].faldon;
                  const maxVersion = Math.max(...currentProducts.map(p => Number(p?.version) || 1));
                  if (maxVersion > 1) {
                    const previousVersion = maxVersion - 1;
                    const previousProduct = previousVersionData.find(
                      item => item.faldon === faldonName && Number(item.version) === previousVersion
                    );
                    if (previousProduct?.fechaHoraCarga && previousProduct?.usernameCarga) {
                      return (
                        <div className="text-muted small mt-2">
                          <strong>Versión anterior:</strong> {formatDateToChileTime(previousProduct.fechaHoraCarga)}{isAdmin && ` subida por ${previousProduct.usernameCarga}`}
                        </div>
                      );
                    }
                  }
                }
                return null;
              })()}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SmuSkirtCards;